import { memo } from 'react';
import type { RefObject } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { SDropdownMenu } from '@setvi/shared/components';
import { QuoteProductDTO } from '@setvi/shared/services/react-query';
import { COLUMN_WIDTHS, COLUMN_MIN_WIDTHS } from '../column-widths';
import { ProductInfo } from './product-info';
import { QuantityColumn, SellColumn, CostColumn, GpColumn } from './columns';
import { FirstRowBox, RowContainer, SecondRowBox } from './styled';
import { ProductOptions } from './product-options';
import { useProductMenuItems } from './hooks/use-product-menu-items';
import { useOptionHandlers } from './hooks/use-option-handlers';
import {
  HIGHLIGHT_BOX_SHADOW,
  QuoteRowPresenceBadge,
  useQuoteRowPresence
} from '../../../pages/details/realtime/quote-row-presence';
import { usePricingLock } from '../../../pages/details/realtime/use-field-locks';

const useStyles = makeStyles(() => ({
  rowContainer: {
    position: 'relative',
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    padding: '12px',
    width: '100%'
  },
  productContainer: {
    display: 'flex',
    gap: '16px'
  },
  productInfoColumn: {
    minWidth: COLUMN_MIN_WIDTHS.productInfo,
    flex: 1
  },
  qtyColumn: {
    width: COLUMN_WIDTHS.qty,
    minWidth: COLUMN_MIN_WIDTHS.qty,
    flexShrink: 1
  },
  unitPriceColumn: {
    width: COLUMN_WIDTHS.unitPrice,
    minWidth: COLUMN_MIN_WIDTHS.unitPrice,
    flexShrink: 1
  },
  gpColumn: {
    width: COLUMN_WIDTHS.gp,
    minWidth: COLUMN_MIN_WIDTHS.gp,
    flexShrink: 1
  },
  totalPriceColumn: {
    width: COLUMN_WIDTHS.totalPrice,
    minWidth: COLUMN_MIN_WIDTHS.totalPrice,
    flexShrink: 1
  },
  actionColumn: {
    width: COLUMN_WIDTHS.action,
    flexShrink: 0
  },
  noteContainer: {
    marginLeft: '80px',
    paddingTop: '12px',
    borderTop: '1px solid #E2E2EA'
  }
}));

interface ProductRowProps {
  product: QuoteProductDTO;
  searchQuery?: string;
  isSectionChild?: boolean;
  onProductClick?: (product: QuoteProductDTO) => void;
  scrollContainerRef?: RefObject<HTMLDivElement>;
  productStartOffset?: number;
}

export const ProductRow = memo(
  ({
    product,
    searchQuery,
    isSectionChild,
    onProductClick,
    scrollContainerRef,
    productStartOffset
  }: ProductRowProps) => {
    const classes = useStyles();
    const productId = product?.id || product?._id;
    const notEditableTooltip = 'Quote is not editable';
    // Presence: show who's on THIS product row and broadcast it on hover.
    const { members, rowProps, isHighlighted } = useQuoteRowPresence(productId);
    // Pricing locks: Unit Price / GP$ / GP% each lock independently (so the
    // editor avatar shows only on the input being edited), but disable together
    // via `pricing.lockedByOther`.
    const pricing = usePricingLock(productId);

    const {
      menuItems,
      isQuoteEditable,
      handleQuantityChange,
      handleSellChange,
      handleProfitMarginChange,
      handleProfitMarginPercentageChange
    } = useProductMenuItems(product);

    // An item is editable when the catalog flags it editable, or it's a
    // user-authored freehand line. When neither (e.g. a non-stocked / locked
    // catalog product), block quantity and price edits even if the quote as a
    // whole is editable. Mirrors the SellColumn rule so all edit surfaces agree.
    const isItemEditable = !!(product?.isEditable || product?.isFreeHand);
    const itemDisabled = !isQuoteEditable || !isItemEditable;
    const itemDisabledTooltip = !isQuoteEditable
      ? notEditableTooltip
      : "This item can't be edited";

    const optionHandlers = useOptionHandlers({
      productId,
      handleQuantityChange,
      handleSellChange,
      handleProfitMarginChange,
      handleProfitMarginPercentageChange
    });

    return (
      <Box
        className={classes.rowContainer}
        {...rowProps}
        style={{
          transition: 'box-shadow 0.4s ease',
          boxShadow: isHighlighted ? HIGHLIGHT_BOX_SHADOW : undefined
        }}>
        <QuoteRowPresenceBadge members={members} />
        <Box className={classes.productContainer}>
          {/* Product Info Column */}
          <Box className={classes.productInfoColumn}>
            <ProductInfo
              badgeText={product?.labelType}
              product={product}
              searchQuery={searchQuery}
              onClick={
                onProductClick ? () => onProductClick(product) : undefined
              }
            />
          </Box>

          {/* Quantity Column */}
          <Box className={classes.qtyColumn}>
            <QuantityColumn
              product={product}
              productId={productId}
              onQuantityChange={handleQuantityChange}
              disabled={itemDisabled}
              disabledTooltip={itemDisabledTooltip}
            />
          </Box>

          {/* Unit Price Column */}
          <Box className={classes.unitPriceColumn}>
            <SellColumn
              product={product}
              productId={productId}
              onSellChange={handleSellChange}
              sellLock={pricing.sell}
              pricingLockedByOther={pricing.lockedByOther}
              disabled={itemDisabled}
              disabledTooltip={itemDisabledTooltip}
            />
          </Box>

          {/* GP Column */}
          <Box className={classes.gpColumn}>
            <GpColumn
              product={product}
              productId={productId}
              onProfitMarginChange={handleProfitMarginChange}
              onProfitMarginPercentageChange={
                handleProfitMarginPercentageChange
              }
              gpLock={pricing.gp}
              gpPercentLock={pricing.gpPercent}
              pricingLockedByOther={pricing.lockedByOther}
              disabled={itemDisabled}
              disabledTooltip={itemDisabledTooltip}
            />
          </Box>

          {/* Total Price Column */}
          <Box className={classes.totalPriceColumn}>
            <CostColumn product={product} />
          </Box>

          {/* Action Column */}
          <Box
            className={classes.actionColumn}
            style={
              isSectionChild
                ? { width: `calc(${COLUMN_WIDTHS.action} - 12px)` }
                : undefined
            }>
            <RowContainer>
              <FirstRowBox>
                <SDropdownMenu
                  newDesign
                  disabled={!isQuoteEditable}
                  disabledTooltip={notEditableTooltip}
                  menuItems={menuItems}
                />
              </FirstRowBox>
              <SecondRowBox hasNote={!!product?.note} />
            </RowContainer>
          </Box>
        </Box>

        {/* Child Products (Options) */}
        <ProductOptions
          product={product}
          isSectionChild={isSectionChild}
          searchQuery={searchQuery}
          onProductClick={onProductClick}
          scrollContainerRef={scrollContainerRef}
          productStartOffset={productStartOffset}
          isQuoteEditable={isQuoteEditable}
          optionHandlers={optionHandlers}
        />
      </Box>
    );
  }
);
